import { useState } from "react";
import { toast } from "sonner";
import { Eye, Save, ArrowLeft } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { Field, Panel, field } from "@/components/admin/shared";
import { PagePicker } from "@/components/decor/constants";

const EMPTY = { name: "", kind: "html", html: "", css: "", js: "", pages: ["all"], position: "top", height: 200, start: "", end: "" };
const Area = ({ label, value, onChange, id }) => (
  <label className="block text-xs uppercase tracking-wider text-mute">{label}
    <textarea className={`${field} mt-1 font-mono text-xs normal-case`} rows={8} spellCheck={false} value={value} onChange={(e) => onChange(e.target.value)} data-testid={id} />
  </label>
);

export const CodeEditor = ({ block, onClose }) => {
  const [f, setF] = useState(block ? Object.fromEntries(Object.keys(EMPTY).map((k) => [k, block[k] ?? EMPTY[k]])) : EMPTY);
  const [prev, setPrev] = useState(null);
  const set = (k) => (v) => setF({ ...f, [k]: v?.target ? v.target.value : v });
  const preview = async () => {
    try {
      const { data } = await api.post("/admin/code-blocks/preview", f);
      setPrev(data.kind === "html" ? `<!doctype html><html><head><meta charset="utf-8"><style>body{margin:0;font-family:sans-serif}${data.css}</style></head><body><div class="lg-code-preview">${data.html}</div></body></html>` : data.srcdoc);
    } catch (e) { toast.error(errorText(e)); setPrev(null); }
  };
  const save = async () => {
    try {
      if (block) await api.put(`/admin/code-blocks/${block.id}`, f); else await api.post("/admin/code-blocks", f);
      toast.success(block ? "Cambios guardados" : "Código guardado (desactivado; actívalo desde la lista)");
      onClose();
    } catch (e) { toast.error(errorText(e)); }
  };
  return (
    <Panel title={block ? "Editar código" : "Nuevo código"} action={<button className="btn-ghost !px-3 !py-2 text-xs" onClick={onClose} data-testid="code-back-btn"><ArrowLeft size={14} /> Volver</button>}>
      <div className="grid gap-4 md:grid-cols-3">
        <Field id="code-name" label="Nombre" value={f.name} onChange={set("name")} />
        <label className="block text-xs uppercase tracking-wider text-mute">Tipo
          <select className={`${field} mt-1 normal-case`} value={f.kind} onChange={set("kind")} data-testid="code-kind">
            <option value="html">HTML / CSS / SVG</option><option value="js">JavaScript aislado (recuadro)</option>
          </select>
        </label>
        <label className="block text-xs uppercase tracking-wider text-mute">Ubicación
          <select className={`${field} mt-1 normal-case`} value={f.position} onChange={set("position")} data-testid="code-position">
            <option value="top">Arriba del contenido</option><option value="bottom">Abajo del contenido</option>
          </select>
        </label>
        <Field id="code-start" label="Mostrar desde (opcional)" type="datetime-local" value={f.start} onChange={set("start")} />
        <Field id="code-end" label="Mostrar hasta (opcional)" type="datetime-local" value={f.end} onChange={set("end")} />
        {f.kind === "js" && <Field id="code-height" label="Alto del recuadro (px)" type="number" min={40} max={900} value={f.height} onChange={(e) => setF({ ...f, height: Number(e.target.value) })} />}
      </div>
      <div className="mt-4"><p className="mb-2 text-xs uppercase tracking-wider text-mute">Páginas</p><PagePicker value={f.pages} onChange={set("pages")} id="code" /></div>
      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Area label="HTML / SVG" value={f.html} onChange={set("html")} id="code-html" />
        <Area label="CSS" value={f.css} onChange={set("css")} id="code-css" />
        {f.kind === "js" && <Area label="JavaScript" value={f.js} onChange={set("js")} id="code-js" />}
      </div>
      <p className="mt-2 text-xs text-mute">No se permiten scripts, eventos (onclick…), formularios, iframes ni recursos externos de JavaScript. En el tipo JavaScript, el código no puede acceder a la página, a tu sesión ni a internet.</p>
      <div className="mt-4 flex flex-wrap gap-2">
        <button className="btn-ghost" onClick={preview} data-testid="code-preview-btn"><Eye size={16} /> Vista previa</button>
        <button className="btn-gold" onClick={save} data-testid="code-save-btn"><Save size={16} /> Guardar</button>
      </div>
      {prev && <iframe title="Vista previa" sandbox={f.kind === "js" ? "allow-scripts" : ""} srcDoc={prev} className="mt-4 w-full rounded-sm border border-line bg-white" style={{ height: f.kind === "js" ? f.height : 260 }} data-testid="code-preview-frame" />}
    </Panel>
  );
};
